import { CheckoutDrawerShell } from '../drawer/checkout_drawer_shell.js';
import type { RequestCheckoutProps, WithRoster } from '../drawer/checkout_subject.js';
import { useCheckoutEnv } from '../drawer/env_context.js';
import { MultiTravelerCheckoutForm } from '../drawer/multi_traveler_checkout_form.js';
import { useCheckoutSession } from '../form/use_checkout_session.js';
import { useConciergeRequest } from '../form/use_concierge_request.js';
import { useMultiTravelerCheckoutForm } from '../form/use_multi_traveler_checkout_form.js';
import { ConciergeRequestCard } from './concierge_request_card.js';
import { ConciergeSummaryPanel } from './concierge_summary_panel.js';
import { requestModule } from './request_registry.js';

/** Total "zero" do pedido: sem preço não há farol de orçamento nem política a
 *  justificar — `withinPolicy` fica `true` de propósito. */
const NO_TOTAL = 0;

/**
 * Modo `request`: o pedido OFFLINE à equipe VOLL (o "concierge" do legado).
 * Desliga preço, farol e política e usa o manifesto do pedido do produto; acima do form de
 * viajantes vem o editor do pedido, e a coluna lateral é o resumo do pedido sem
 * preço. Não passa pelo `CheckoutItem`: um item sem oferta e sem valor violaria
 * o contrato do carrinho e cada regra de lá (soma, farol, política) teria de
 * aprender um caso especial.
 */
export function RequestCheckout({
  open,
  readOnly = false,
  subject,
  travelers,
  rosterFailed,
  requesterId,
  policyTravelerId,
  tripId,
  allocationOwner: hostAllocationOwner,
  initialDraft,
  // O pedido nunca fecha sozinho: o host mostra o envio e a tela de enviado.
  closeOnSubmit = false,
  submitting: hostSubmitting,
  submitNotice,
  onClose,
  onSubmit,
}: Readonly<WithRoster<RequestCheckoutProps>>) {
  const env = useCheckoutEnv();
  const { copy } = env;
  const { request } = subject;
  const product = requestModule(request);
  const fields = env.rules.conciergeFields(request.productType);
  const allocationOwner = hostAllocationOwner ?? product.allocationOwner;
  const form = useMultiTravelerCheckoutForm({
    io: env.io,
    rules: env.rules,
    productType: request.productType,
    fields,
    open,
    travelers,
    requesterId,
    policyTravelerId,
    withinPolicy: true,
    allocationOwner,
    total: NO_TOTAL,
    startDate: product.startDate(request),
    ...(initialDraft ? { initialDraft } : {}),
  });
  const concierge = useConciergeRequest({
    io: env.io,
    productType: request.productType,
    segmentCount: product.segmentCount(request),
    initialOnlyDirect: request.profile.some(
      (chip) => chip.toLowerCase() === copy.concierge.directChip.toLowerCase(),
    ),
    categoryHint: request.productType === 'rental' ? request.categoryHint : undefined,
  });
  const session = useCheckoutSession({ form, hostSubmitting, closeOnSubmit, onClose });
  const canSubmit = !readOnly && form.canSubmit && concierge.canSubmit;

  if (!open) return null;

  const handleSubmit = () => {
    if (session.submitting || !canSubmit) return;
    const submission = {
      perTraveler: session.travelerEntries(),
      motiveId: form.motiveId,
      justificationId: form.justificationId,
      observation: form.observation,
      policyTravelerId: form.policyTravelerId,
      allocationOwner,
      requesterId,
    };
    session.send(() => {
      onSubmit({ mode: 'request', form: submission, concierge });
    });
  };

  // O pedido em si não é rascunhado: reabrir recomeça dele. Só o rascunho
  // corporativo volta ao host, como no modo de oferta.
  return (
    <CheckoutDrawerShell
      phase={rosterFailed ? 'error' : form.phase}
      // Só o form que caiu se refaz (#6644); o roster que falhou fica no ✕.
      {...(!rosterFailed && form.phase === 'error' ? { onRetry: form.reload } : {})}
      items={[]}
      title={copy.concierge.title}
      chip={product.chip(request, copy.concierge)}
      total={{ amount: NO_TOTAL, currency: 'BRL' }}
      submitting={session.submitting}
      canSubmit={canSubmit}
      onSubmit={handleSubmit}
      onClose={session.close}
      side={
        <ConciergeSummaryPanel
          request={request}
          travelers={travelers}
          tripId={tripId}
          submitting={session.submitting}
          canSubmit={form.phase === 'ready' && canSubmit}
          submitNotice={submitNotice}
          onSubmit={handleSubmit}
          onBack={session.close}
        />
      }
    >
      <ConciergeRequestCard request={request} state={concierge} />
      <MultiTravelerCheckoutForm
        fields={fields}
        state={form}
        requesterId={requesterId}
        withinPolicy
        total={NO_TOTAL}
        allocationOwner={allocationOwner}
        allocationHint={copy.traveler.allocationFromRequester}
      />
    </CheckoutDrawerShell>
  );
}
